import { customElement } from 'lit/decorators.js'
import maplibregl from 'maplibre-gl'
import { BaseShape, type IndexedMarker } from './base-shape.js'
import { isGeoJSONSource } from '../map-engine.js'
import { db } from '../../../database/schema.js'
import { getFillPaintProperties } from './fill-patterns.js'
import { calculatePolygonCentroid, translatePolygonVertices } from '../../../lib/shape-utils.js'
import { addShapeLayer } from '../layer-order-manager.js'

@customElement('shape-polygon')
export class ShapePolygon extends BaseShape {
  private vertexHandles: maplibregl.Marker[] = []
  private midpointHandles: maplibregl.Marker[] = []
  private draggedVertexIndex?: number | undefined
  private draggedMidpointIndex?: number | undefined

  protected createLayers(): void {
    const geojson = this.getGeoJSON()
    this.map.addSource(this.sourceId, { type: 'geojson', data: geojson })

    const fillLayerId = `${this.sourceId}-fill`
    const strokeLayerId = `${this.sourceId}-stroke`

    const fillProps = getFillPaintProperties(this.feature.style, '#3f5a7a')

    addShapeLayer(this.map, {
      id: fillLayerId,
      type: 'fill',
      source: this.sourceId,
      paint: {
        'fill-color': fillProps.fillColor,
        'fill-opacity': fillProps.fillOpacity,
      },
    })

    const dashArray = this.feature.style?.strokeDashArray ?? undefined
    addShapeLayer(this.map, {
      id: strokeLayerId,
      type: 'line',
      source: this.sourceId,
      paint: {
        'line-color': this.feature.style?.strokeColor ?? '#3f5a7a',
        'line-width': this.feature.style?.strokeWidth ?? 2,
        'line-opacity': this.feature.style?.strokeOpacity ?? 0.8,
        ...(dashArray ? { 'line-dasharray': dashArray } : {}),
      },
    })

    this.layerIds = [fillLayerId, strokeLayerId]
  }

  getGeoJSON(): GeoJSON.Feature {
    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: (this.feature.geometry as GeoJSON.Polygon).coordinates,
      },
    }
  }

  override refreshStyle(): void {
    const fillLayerId = `${this.sourceId}-fill`
    const strokeLayerId = `${this.sourceId}-stroke`
    const fillProps = getFillPaintProperties(this.feature.style, '#3f5a7a')

    if (this.map.getLayer(fillLayerId)) {
      this.map.setPaintProperty(fillLayerId, 'fill-color', fillProps.fillColor)
      this.map.setPaintProperty(fillLayerId, 'fill-opacity', fillProps.fillOpacity)
    }
    if (this.map.getLayer(strokeLayerId)) {
      this.map.setPaintProperty(
        strokeLayerId,
        'line-color',
        this.feature.style?.strokeColor ?? '#3f5a7a'
      )
      this.map.setPaintProperty(strokeLayerId, 'line-width', this.feature.style?.strokeWidth ?? 2)
      this.map.setPaintProperty(
        strokeLayerId,
        'line-opacity',
        this.feature.style?.strokeOpacity ?? 0.8
      )
      const dashArray = this.feature.style?.strokeDashArray
      this.map.setPaintProperty(
        strokeLayerId,
        'line-dasharray',
        dashArray?.length ? dashArray : (null as number[] | null)
      )
    }
    this.applyFillPatternToLayer()
  }

  updateSource(): void {
    const source = this.map.getSource(this.sourceId)
    if (isGeoJSONSource(source)) {
      source.setData(this.getGeoJSON())
    }
  }

  protected createHandles(): void {
    this.vertexHandles = []
    this.midpointHandles = []

    const coords = (this.feature.geometry as GeoJSON.Polygon).coordinates[0]
    const vertices = coords.slice(0, -1)

    vertices.forEach((coord: number[], index: number) => {
      const vertexHandle = this.createHandle([coord[0], coord[1]], 'vertex') as IndexedMarker
      vertexHandle._vertexIndex = index
      this.vertexHandles.push(vertexHandle)
      this.handles.push(vertexHandle)

      const nextIndex = (index + 1) % vertices.length
      const nextCoord = vertices[nextIndex]
      const midpoint: [number, number] = [
        (coord[0] + nextCoord[0]) / 2,
        (coord[1] + nextCoord[1]) / 2,
      ]
      const midpointHandle = this.createHandle(midpoint, 'midpoint') as IndexedMarker
      midpointHandle._midpointIndex = index
      this.midpointHandles.push(midpointHandle)
      this.handles.push(midpointHandle)
    })

    this.setupPolygonDragging()
  }

  protected override setupHandleDragging(marker: maplibregl.Marker, handleType: string): void {
    const element = marker.getElement()
    const indexed = marker as IndexedMarker
    const initDrag = (): void => {
      if (handleType === 'vertex') {
        this.draggedVertexIndex = indexed._vertexIndex
      } else if (handleType === 'midpoint') {
        this.draggedMidpointIndex = indexed._midpointIndex
      }
      this.startHandleDrag(marker, handleType)
    }
    element.addEventListener('mousedown', (e): void => {
      e.stopPropagation()
      e.preventDefault()
      initDrag()
    })
    element.addEventListener(
      'touchstart',
      (e): void => {
        e.preventDefault()
        e.stopPropagation()
        initDrag()
      },
      { passive: false }
    )
  }

  protected updateShapePreview(handleType: string, newPosition: maplibregl.LngLat): void {
    if (handleType === 'drag') {
      const coords = (this.feature.geometry as GeoJSON.Polygon).coordinates[0]
      const vertices = coords.slice(0, -1)
      const [centerLng, centerLat] = calculatePolygonCentroid(vertices)
      const offsetLng = newPosition.lng - centerLng
      const offsetLat = newPosition.lat - centerLat
      const newVerts = translatePolygonVertices(vertices, offsetLng, offsetLat)
      const closed = [...newVerts, newVerts[0]]
      const source = this.map.getSource(this.sourceId)
      if (isGeoJSONSource(source)) {
        source.setData({
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: [closed] },
        })
      }
    } else if (handleType === 'vertex' && this.draggedVertexIndex !== undefined) {
      const coords = (this.feature.geometry as GeoJSON.Polygon).coordinates[0]
      const vertices = [...coords.slice(0, -1)]
      vertices[this.draggedVertexIndex] = [newPosition.lng, newPosition.lat]
      const closed = [...vertices, vertices[0]]
      const source = this.map.getSource(this.sourceId)
      if (isGeoJSONSource(source)) {
        source.setData({
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: [closed] },
        })
      }
    } else if (handleType === 'midpoint' && this.draggedMidpointIndex !== undefined) {
      const coords = (this.feature.geometry as GeoJSON.Polygon).coordinates[0]
      const vertices = [...coords.slice(0, -1)]
      vertices.splice(this.draggedMidpointIndex + 1, 0, [newPosition.lng, newPosition.lat])
      const closed = [...vertices, vertices[0]]
      const source = this.map.getSource(this.sourceId)
      if (isGeoJSONSource(source)) {
        source.setData({
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: [closed] },
        })
      }
    }
  }

  protected async applyGeometryChange(
    handleType: string,
    newPosition: maplibregl.LngLat
  ): Promise<void> {
    if (handleType === 'drag') {
      const coords = (this.feature.geometry as GeoJSON.Polygon).coordinates[0]
      const vertices = coords.slice(0, -1)
      const [centerLng, centerLat] = calculatePolygonCentroid(vertices)
      const offsetLng = newPosition.lng - centerLng
      const offsetLat = newPosition.lat - centerLat
      const newVerts = translatePolygonVertices(vertices, offsetLng, offsetLat)
      const closed = [...newVerts, newVerts[0]]
      const newGeometry: GeoJSON.Polygon = { type: 'Polygon', coordinates: [closed] }
      await db.features.update(this.feature.id, {
        geometry: newGeometry,
        updatedAt: new Date().toISOString(),
      })
      this.feature.geometry = newGeometry
    } else if (handleType === 'vertex' && this.draggedVertexIndex !== undefined) {
      const coords = (this.feature.geometry as GeoJSON.Polygon).coordinates[0]
      const vertices = [...coords.slice(0, -1)]
      vertices[this.draggedVertexIndex] = [newPosition.lng, newPosition.lat]
      const closed = [...vertices, vertices[0]]
      const newGeometry: GeoJSON.Polygon = { type: 'Polygon', coordinates: [closed] }
      await db.features.update(this.feature.id, {
        geometry: newGeometry,
        updatedAt: new Date().toISOString(),
      })
      this.feature.geometry = newGeometry
      this.draggedVertexIndex = undefined
    } else if (handleType === 'midpoint' && this.draggedMidpointIndex !== undefined) {
      const coords = (this.feature.geometry as GeoJSON.Polygon).coordinates[0]
      const vertices = [...coords.slice(0, -1)]
      vertices.splice(this.draggedMidpointIndex + 1, 0, [newPosition.lng, newPosition.lat])
      const closed = [...vertices, vertices[0]]
      const newGeometry: GeoJSON.Polygon = { type: 'Polygon', coordinates: [closed] }
      await db.features.update(this.feature.id, {
        geometry: newGeometry,
        updatedAt: new Date().toISOString(),
      })
      this.feature.geometry = newGeometry
      this.draggedMidpointIndex = undefined
    }
  }

  protected updateAllHandlePositions(centerPosition: maplibregl.LngLat): void {
    const coords = (this.feature.geometry as GeoJSON.Polygon).coordinates[0]
    const vertices = coords.slice(0, -1)
    const [centerLng, centerLat] = calculatePolygonCentroid(vertices)
    const offsetLng = centerPosition.lng - centerLng
    const offsetLat = centerPosition.lat - centerLat
    this.vertexHandles.forEach((handle, index) => {
      if (index < vertices.length) {
        handle.setLngLat([vertices[index][0] + offsetLng, vertices[index][1] + offsetLat])
      }
    })
    this.midpointHandles.forEach((handle, index) => {
      if (index < vertices.length) {
        const nextIndex = (index + 1) % vertices.length
        handle.setLngLat([
          (vertices[index][0] + vertices[nextIndex][0]) / 2 + offsetLng,
          (vertices[index][1] + vertices[nextIndex][1]) / 2 + offsetLat,
        ])
      }
    })
  }

  private setupPolygonDragging(): void {
    const fillLayerId = `${this.sourceId}-fill`

    this.map.on('mousedown', fillLayerId, e => {
      if (!this.isEditing) {
        return
      }
      e.preventDefault()
      this.startShapeDrag(e.lngLat)
    })
  }

  private _dragMouseMove?: ((e: MouseEvent) => void) | undefined
  private _dragMouseUp?: ((e: MouseEvent) => void) | undefined
  private _dragTouchMove?: ((e: TouchEvent) => void) | undefined
  private _dragTouchEnd?: ((e: TouchEvent) => void) | undefined
  private startShapeDrag(_startPosition: maplibregl.LngLat): void {
    if (this.isDragging) {
      return
    }
    this.isDragging = true
    this.handles.forEach(h => (h.getElement().style.display = 'none'))

    this._dragMouseMove = (e: MouseEvent) => {
      const rect = this.map.getContainer().getBoundingClientRect()
      const lngLat = this.map.unproject([e.clientX - rect.left, e.clientY - rect.top])
      this.updateShapePreview('drag', lngLat)
    }
    this._dragMouseUp = (e: MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      const rect = this.map.getContainer().getBoundingClientRect()
      const lngLat = this.map.unproject([e.clientX - rect.left, e.clientY - rect.top])
      void this.endShapeDrag(lngLat)
    }
    this._dragTouchMove = (e: TouchEvent) => {
      if (!this.isDragging || e.touches.length !== 1) {
        return
      }
      const lngLat = this.touchToLngLat(e.touches[0])
      e.preventDefault()
      this.updateShapePreview('drag', lngLat)
    }
    this._dragTouchEnd = (e: TouchEvent) => {
      if (!this.isDragging) {
        return
      }
      const lngLat = e.changedTouches.length > 0 ? this.touchToLngLat(e.changedTouches[0]) : null
      if (!lngLat) {
        return
      }
      e.preventDefault()
      void this.endShapeDrag(lngLat)
    }

    document.addEventListener('mousemove', this._dragMouseMove)
    document.addEventListener('mouseup', this._dragMouseUp)
    this.map.getContainer().addEventListener('touchmove', this._dragTouchMove, { passive: false })
    this.map.getContainer().addEventListener('touchend', this._dragTouchEnd, { passive: false })
  }

  private async endShapeDrag(newPosition: maplibregl.LngLat): Promise<void> {
    if (!this.isDragging) {
      return
    }
    this.isDragging = false
    BaseShape.ignoreNextMapClick++
    setTimeout(() => {
      if (BaseShape.ignoreNextMapClick > 0) {
        BaseShape.ignoreNextMapClick = 0
      }
    }, 200)

    if (this._dragMouseMove) {
      document.removeEventListener('mousemove', this._dragMouseMove)
    }
    if (this._dragMouseUp) {
      document.removeEventListener('mouseup', this._dragMouseUp)
    }
    if (this._dragTouchMove) {
      this.map.getContainer().removeEventListener('touchmove', this._dragTouchMove)
    }
    if (this._dragTouchEnd) {
      this.map.getContainer().removeEventListener('touchend', this._dragTouchEnd)
    }
    this._dragMouseMove = undefined
    this._dragMouseUp = undefined
    this._dragTouchMove = undefined
    this._dragTouchEnd = undefined

    await this.applyGeometryChange('drag', newPosition)
    if (this.isEditing) {
      this.removeHandles()
      this.createHandles()
    }
  }

  protected override applySelectionStyle(selected: boolean): void {
    const strokeLayerId = `${this.sourceId}-stroke`
    if (this.map.getLayer(strokeLayerId)) {
      this.map.setPaintProperty(
        strokeLayerId,
        'line-width',
        selected
          ? (this.feature.style?.strokeWidth ?? 2) + 2
          : (this.feature.style?.strokeWidth ?? 2)
      )
    }
  }
}
